<script setup lang="ts">
import type { Category, Deal, Suggestion } from '~/types/api'

const { $api } = useApi()

// New random order on every visit; the server-rendered payload is reused on hydration so it doesn't flicker
const fresh = (key: string, nuxtApp: ReturnType<typeof useNuxtApp>, ctx: { cause: string }) =>
  ctx.cause === 'initial' ? nuxtApp.payload.data[key] : undefined

const [{ data: categories }, { data: trending }] = await Promise.all([
  useAsyncData('home-categories', () => $api<Category[]>('/api/categories?shuffle=true'), {
    default: () => [],
    getCachedData: fresh,
  }),
  useAsyncData('home-trending', () => $api<Deal[]>('/api/deals/trending?shuffle=true'), {
    default: () => [],
    getCachedData: fresh,
  }),
])

const query = ref('')
const { suggestions } = useSearchSuggestions(query)

function search(q: string) {
  if (q) navigateTo({ path: '/search', query: { q } })
}

function pick(s: Suggestion) {
  if (s.category_slug) return navigateTo(`/find/${s.category_slug}`)
  return search(s.label)
}

const TESTIMONIALS = [
  { name: 'Carmen', quote: 'Shared a TV deal with my family group and we all saved another 6%.' },
  { name: 'Koray', quote: 'Told it my budget, it found a Smart TV I’d never have spotted myself.' },
  { name: 'Amanda', quote: 'The countdown and price drops make it feel like a game I keep winning.' },
]

const BENEFITS = [
  'Tell us what you want — we hunt the deals',
  'Prices drop as more people join',
  'Lock in your price with a small deposit',
]
</script>

<template>
  <div>
    <section class="hero">
      <div class="container">
        <h1 class="display hero__title">Find discounts on products you love by the power of AI</h1>
        <SearchBox v-model="query" class="hero__search" @submit="search" />
        <div v-if="suggestions.length" class="chips hero__suggest" aria-live="polite">
          <button v-for="s in suggestions" :key="`${s.type}-${s.label}`" type="button" class="chip" @click="pick(s)">
            {{ s.label }}
          </button>
        </div>
        <p class="text-sm muted hero__or">or search by category</p>
        <div class="chips">
          <NuxtLink v-for="c in categories" :key="c.slug" :to="`/find/${c.slug}`" class="chip">{{ c.name }}</NuxtLink>
        </div>
      </div>
    </section>

    <section id="trending" class="section">
      <div class="container">
        <h2 class="h1 section__title">Trending</h2>
        <div class="scroller">
          <DealCard v-for="d in trending" :key="d.id" :deal="d" class="card card-pad" />
        </div>
      </div>
    </section>

    <section id="categories" class="section">
      <div class="container">
        <h2 class="h1 section__title">Categories</h2>
        <div class="scroller">
          <NuxtLink v-for="c in categories" :key="c.slug" :to="`/find/${c.slug}`" class="cat card">
            <div class="cat__media">
              <ProductImage :src="c.image_url" :alt="c.name" />
            </div>
            <div class="cat__body">
              <h3 class="h3">{{ c.name }}</h3>
              <p class="text-sm muted">{{ c.question }}</p>
            </div>
          </NuxtLink>
        </div>
      </div>
    </section>

    <section class="section">
      <div class="container">
        <h2 class="text-md semibold">What shoppers say</h2>
        <ul class="quotes">
          <li v-for="t in TESTIMONIALS" :key="t.name">
            <p class="text-md">“{{ t.quote }}”</p>
            <p class="text-sm semibold quotes__name">{{ t.name }}</p>
          </li>
        </ul>
      </div>
    </section>

    <section class="section">
      <div class="container trial">
        <div>
          <h2 class="h1">How Regotiate works</h2>
          <p class="text-md muted trial__lead">
            Group buying for electronics. The more people join a deal, the lower the price gets — for everyone.
          </p>
          <ul class="benefits">
            <li v-for="b in BENEFITS" :key="b">
              <span class="benefits__tick"><AppIcon name="check" :size="14" :stroke-width="3" /></span>
              {{ b }}
            </li>
          </ul>
        </div>
        <div class="trial__card">
          <h2 class="display trial__heading">Start finding deals</h2>
          <p class="text-md">Tell us what you’re after and your budget. We’ll do the rest.</p>
          <NuxtLink to="/search" class="btn btn-primary btn-block">Get started</NuxtLink>
          <NuxtLink to="/find/television" class="btn btn-primary btn-block">Browse TV deals</NuxtLink>
        </div>
      </div>
    </section>
  </div>
</template>

<style scoped>
.hero {
  padding: 32px 0 16px;
  background: var(--gray-50);
}

.hero__title {
  max-width: 640px;
  font-size: 24px;
  line-height: 32px;
}

.hero__search {
  margin-top: 24px;
  max-width: 640px;
}

.hero__suggest {
  margin-top: 16px;
}

.hero__or {
  margin: 24px 0 12px;
}

.section {
  padding: 32px 0 8px;
}

.section__title {
  margin-bottom: 16px;
}

.cat {
  display: block;
  overflow: hidden;
}

.cat__media {
  aspect-ratio: 4 / 3;
}

.cat__body {
  padding: 16px;
}

.cat__body p {
  margin-top: 4px;
}

.quotes {
  list-style: none;
  margin: 12px 0 0;
  padding: 0;
  display: grid;
  gap: 0;
}

.quotes li {
  padding: 20px 0;
  border-bottom: 1px solid var(--gray-200);
}

.quotes__name {
  margin-top: 8px;
  color: var(--gray-700);
}

.trial {
  display: grid;
  gap: 32px;
}

.trial__lead {
  margin-top: 8px;
}

.benefits {
  list-style: none;
  margin: 24px 0 0;
  padding: 0;
  display: grid;
  gap: 16px;
  color: var(--gray-600);
}

.benefits li {
  display: flex;
  align-items: center;
  gap: 12px;
}

.benefits__tick {
  flex: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: var(--error-50);
  color: var(--logo-red);
}

.trial__card {
  display: grid;
  gap: 16px;
  padding: 40px 24px;
  border-radius: var(--radius-lg);
  background: var(--gray-50);
  color: var(--brand-700);
}

.trial__heading {
  color: var(--brand-900);
}

.trial__card .btn {
  margin-top: 8px;
}

@media (min-width: 768px) {
  .hero {
    padding: 64px 0 32px;
  }

  .hero__title {
    font-size: 36px;
    line-height: 44px;
  }

  .quotes {
    grid-template-columns: repeat(3, 1fr);
    gap: 24px;
  }

  .trial {
    grid-template-columns: 1fr 1fr;
    align-items: center;
  }
}
</style>
